import { type UseQueryOptions, type UseQueryResult, useQuery } from '@tanstack/react-query';

import { useReduxState } from '@/app/shared/context/ReduxStoreContext';

import { useQueryStateAppContext } from '@/packages/query-state/shared/context/appContext';
import { completeJsonQueryByPinSelector } from '@/packages/redux-state/store/slices/jsonQuery';
import { useActivePinListingIdSelector } from '@/packages/redux-state/store/slices/mapControls/selectors/dumb';
import { getCancellationTokenFromSignal } from '@/packages/utils/getCancellationTokenFromSignal';

import {
  type TGetOffersByPinRequest,
  type TGetOffersByPinResponse_1,
} from '@/repositories/generated/shared/search-offers/v1/get-offers-by-pin';

import { type QueryError, type QueryKey, getQueryKey } from './internal/config';
import { fetchGetOffersByPinService } from './internal/fetchService';

type UseGetOffersByPinQueryOptions<TData> = Omit<
  UseQueryOptions<TGetOffersByPinResponse_1, QueryError, TData, QueryKey>,
  'queryFn' | 'queryKey'
>;

type UseGetOffersByPinQueryParams = {
  id: TGetOffersByPinRequest['id'];
  getJsonQuery: () => TGetOffersByPinRequest['jsonQuery'];
};

export function useGetOffersByPinQuery<TData = TGetOffersByPinResponse_1>(
  parameters: UseGetOffersByPinQueryParams,
  options?: UseGetOffersByPinQueryOptions<TData>,
): UseQueryResult<TData, QueryError> {
  const applicationContext = useQueryStateAppContext();

  return useQuery<TGetOffersByPinResponse_1, QueryError, TData, QueryKey>({
    queryFn: ({ signal }) =>
      fetchGetOffersByPinService({
        applicationContext,
        cancellationToken: getCancellationTokenFromSignal(signal),
        parameters: {
          id: parameters.id,
          jsonQuery: parameters.getJsonQuery(),
        },
      }),
    queryKey: getQueryKey(parameters.id),
    ...options,
  });
}

type UseGetOffersByActivePinQueryOptions<TData> = Omit<
  UseGetOffersByPinQueryOptions<TData>,
  'enabled' | 'keepPreviousData'
>;

export function useGetOffersByActivePinQuery<TData = TGetOffersByPinResponse_1>(
  options?: UseGetOffersByActivePinQueryOptions<TData>,
): UseQueryResult<TData, QueryError> {
  const listingPinId = useActivePinListingIdSelector();
  const reduxState = useReduxState();

  return useGetOffersByPinQuery(
    { getJsonQuery: () => completeJsonQueryByPinSelector(reduxState.getState()), id: listingPinId as string },
    {
      ...options,
      enabled: !!listingPinId,
      keepPreviousData: true,
    },
  );
}
